import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './CaseManagement.css';

const CaseRegistration = () => {
  const { cases, setCases, courts, judges, addAuditLog } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    caseType: 'Civil',
    caseTitle: '',
    filingDate: new Date().toISOString().split('T')[0],
    courtId: '',
    judgeId: '',
    description: '',
    status: 'Pending',
  });
  const [editingId, setEditingId] = useState(null);

  const caseTypes = ['Civil', 'Criminal', 'Family', 'Corporate', 'Property', 'Labor'];

  const generateCaseNumber = (caseType, filingDate) => {
    const year = new Date(filingDate).getFullYear();
    const typeCode = caseType.substring(0, 3).toUpperCase();
    const count = cases.filter(c => 
      c.caseType === caseType && 
      new Date(c.filingDate).getFullYear() === year
    ).length + 1;
    return `${typeCode}/${year}/${String(count).padStart(4, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const court = courts.find(c => c.id === formData.courtId);
    const judge = judges.find(j => j.id === formData.judgeId);
    
    const caseNumber = editingId 
      ? cases.find(c => c.id === editingId).caseNumber
      : generateCaseNumber(formData.caseType, formData.filingDate);
    
    if (editingId) {
      setCases(cases.map(c => 
        c.id === editingId 
          ? { 
              ...c, 
              ...formData, 
              courtName: court?.courtName || c.courtName,
              judgeName: judge?.judgeName || c.judgeName,
            }
          : c
      ));
      addAuditLog('Case Updated', `Case ${caseNumber} updated`);
    } else {
      const newCase = {
        id: Date.now().toString(),
        caseNumber,
        ...formData,
        courtName: court?.courtName || '',
        judgeName: judge?.judgeName || '',
        createdAt: new Date().toISOString(),
      };
      setCases([...cases, newCase]);
      addAuditLog('Case Registered', `New case ${caseNumber} registered`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      caseType: 'Civil',
      caseTitle: '',
      filingDate: new Date().toISOString().split('T')[0],
      courtId: '',
      judgeId: '',
      description: '',
      status: 'Pending',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (caseItem) => {
    setFormData(caseItem);
    setEditingId(caseItem.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const caseItem = cases.find(c => c.id === id);
    setCases(cases.filter(c => c.id !== id));
    addAuditLog('Case Deleted', `Case ${caseItem.caseNumber} deleted`);
  };

  return (
    <div className="case-container">
      <div className="case-header">
        <h1>📁 Case Registration</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Register New Case'}
        </button>
      </div>

      {showForm && (
        <div className="case-form-card">
          <h2>{editingId ? 'Edit Case' : 'Register New Case'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Case Type *</label>
                <select
                  value={formData.caseType}
                  onChange={(e) => setFormData({...formData, caseType: e.target.value})}
                  required
                >
                  {caseTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Case Title *</label>
                <input
                  type="text"
                  value={formData.caseTitle}
                  onChange={(e) => setFormData({...formData, caseTitle: e.target.value})}
                  required
                  placeholder="Enter case title"
                />
              </div>

              <div className="form-group">
                <label>Filing Date *</label>
                <input
                  type="date"
                  value={formData.filingDate}
                  onChange={(e) => setFormData({...formData, filingDate: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Court *</label>
                <select
                  value={formData.courtId}
                  onChange={(e) => setFormData({...formData, courtId: e.target.value})}
                  required
                >
                  <option value="">Select Court</option>
                  {courts.map(court => (
                    <option key={court.id} value={court.id}>{court.courtName}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Judge *</label>
                <select
                  value={formData.judgeId}
                  onChange={(e) => setFormData({...formData, judgeId: e.target.value})}
                  required
                >
                  <option value="">Select Judge</option>
                  {judges.map(judge => (
                    <option key={judge.id} value={judge.id}>{judge.judgeName} - {judge.courtName}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  required
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Disposed">Disposed</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="4"
                  placeholder="Enter case description"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Register'} Case
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="case-table-card">
        <h2>Registered Cases ({cases.length})</h2>
        {cases.length > 0 ? (
          <div className="table-container">
            <table className="case-table">
              <thead>
                <tr>
                  <th>Case Number</th>
                  <th>Case Title</th>
                  <th>Type</th>
                  <th>Court</th>
                  <th>Judge</th>
                  <th>Filing Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cases.map(caseItem => (
                  <tr key={caseItem.id}>
                    <td><strong>{caseItem.caseNumber}</strong></td>
                    <td>{caseItem.caseTitle}</td>
                    <td><span className="case-type-badge">{caseItem.caseType}</span></td>
                    <td>{caseItem.courtName}</td>
                    <td>{caseItem.judgeName}</td>
                    <td>{new Date(caseItem.filingDate).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge status-${caseItem.status.toLowerCase().replace(' ', '-')}`}>
                        {caseItem.status}
                      </span>
                    </td>
                    <td>
                      <button onClick={() => handleEdit(caseItem)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(caseItem.id)} className="btn-delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No cases registered yet. Click "Register New Case" to get started.</p>
        )}
      </div>
    </div>
  );
};

export default CaseRegistration;

